import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_TOKEN_KEY = "lottoops_token";

// All requests must send/receive the httpOnly auth cookie
axios.defaults.withCredentials = true;

// Legacy migration: keep using a localStorage Bearer token for the current
// session if one exists from before the cookie migration. On next
// login/register/logout we switch fully to cookies.
const legacyToken = typeof window !== "undefined" ? localStorage.getItem(LEGACY_TOKEN_KEY) : null;
if (legacyToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${legacyToken}`;
}

const dropLegacy = () => {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  delete axios.defaults.headers.common["Authorization"];
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cancel the initial /auth/me request when the user logs in / registers,
  // so a stale 401 cannot wipe out the freshly authenticated state.
  const initialAbort = useRef(null);
  const pausedToastAt = useRef(0);

  useEffect(() => {
    // 401 interceptor:
    // We deliberately DO NOT auto-logout on 401 for arbitrary protected routes,
    // because a transient 401 (cookie-propagation race after login, a single
    // failed endpoint, a stale browser cache, etc.) would otherwise kick the
    // user back to /login mid-session. Session expiry is already handled on
    // page reload via fetchUser() → /auth/me.
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Paused (view-only) accounts: surface blocked saves loudly, since
        // some writes (e.g. autosave) fail silently otherwise. Rate-limited.
        const detail = error?.response?.status === 403 ? error?.response?.data?.detail : null;
        if (typeof detail === "string" && detail.includes("paused")) {
          const now = Date.now();
          if (now - (pausedToastAt.current || 0) > 4000) {
            pausedToastAt.current = now;
            toast.error(detail, { id: "paused-403" });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchUser = useCallback(async () => {
    initialAbort.current = new AbortController();
    try {
      const res = await axios.get(`${API}/auth/me`, { signal: initialAbort.current.signal });
      setUser(res.data);
    } catch (err) {
      // Aborted (user logged in concurrently) → leave state alone
      if (axios.isCancel(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      // 401/other → user just not logged in; loading flip below will reveal the login page
      dropLegacy();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    // Cancel any in-flight /auth/me from initial mount so its stale 401
    // cannot nuke our about-to-be-set user.
    initialAbort.current?.abort();
    const res = await axios.post(`${API}/auth/login`, { email, password });
    dropLegacy();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (name, email, password, access_code, extra = {}) => {
    initialAbort.current?.abort();
    const res = await axios.post(`${API}/auth/register`, { name, email, password, access_code, ...extra });
    dropLegacy();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const switchStore = useCallback(async (storeId) => {
    await axios.post(`${API}/user/switch-store`, { store_id: storeId });
    const fresh = await axios.get(`${API}/auth/me`);
    setUser(fresh.data);
    return fresh.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Even if the server call fails, we still clear local state below.
      if (process.env.NODE_ENV === "development") console.warn("Logout request failed:", err);
    }
    dropLegacy();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, logout, refreshUser, switchStore, isAuthenticated: !!user
  }), [user, loading, login, register, logout, refreshUser, switchStore]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
